import React from "react";
import PropTypes from "prop-types";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import businessFormStyles from "assets/jss/material-dashboard-react/components/businessForm";

const useStyles = makeStyles(theme => ({
  ...businessFormStyles(theme),
  filterPanel: {
    paddingRight: "34px",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: "transparent",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  headerTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
  fieldSection: {
    marginBottom: theme.spacing(1),
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  dateRangeLabel: {
    display: "block",
    color: theme.palette.primary.dark,
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginLeft: 0,
    paddingLeft: 0,
  },
  dateRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    alignItems: "start",
    margin: 0,
    padding: 0,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  fullWidthField: {
    width: "100%",
    margin: 0,
    padding: 0,
  },
}));

const mergeDatePickerInputProps = (inputProps, classes) => {
  const {
    InputProps: pickerInputProps = {},
    InputLabelProps: pickerInputLabelProps = {},
    className: _inputClassName,
    label: _inputLabel,
    style: _inputStyle,
    required: _inputRequired,
    ...restProps
  } = inputProps;

  return {
    ...restProps,
    InputLabelProps: {
      ...pickerInputLabelProps,
      classes: {
        ...pickerInputLabelProps?.classes,
        root: classes.activeInputLableColor,
        focused: "focused",
      },
    },
    InputProps: {
      ...pickerInputProps,
      classes: {
        ...pickerInputProps?.classes,
        root: classes.activeInputColor,
        focused: "focused",
        input: classes.resize,
      },
    },
  };
};

function NachaFilterPopover({
  values,
  onChange,
  onDateChange,
  onSearch,
  onClear,
  searchText = "",
  startDateValue,
  endDateValue,
  dateRangeError,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const hasFilters = Boolean(searchText.trim()) || Object.values(values).some(value => value !== "");
  const popoverProps = {
    disablePortal: false,
    container: typeof document !== "undefined" ? document.body : undefined,
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "center",
    },
    style: {
      zIndex: theme.zIndex.modal + 2,
    },
    PaperProps: {
      style: {
        zIndex: theme.zIndex.modal + 2,
      },
    },
  };

  const selectMenuProps = {
    MenuProps: {
      disablePortal: false,
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
    },
  };

  return (
    <div className={classes.filterPanel}>
      <form onSubmit={onSearch}>
        <Grid container spacing={0} style={{ margin: 0, width: "100%", padding: 0 }}>
          <Grid item xs={12} className={classes.fieldSection} style={{ padding: 0 }}>
            <Typography className={classes.dateRangeLabel}>Date Range</Typography>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <div className={classes.dateRow}>
                <DatePicker
                  autoOk
                  variant="inline"
                  label="Start Date"
                  value={values.startDate ? new Date(values.startDate) : null}
                  onChange={onDateChange("startDate")}
                  format="yyyy/MM/dd"
                  maxDate={endDateValue || new Date()}
                  style={{ width: "100%" }}
                  PopoverProps={popoverProps}
                >
                  {inputProps => (
                    <TextField
                      {...mergeDatePickerInputProps(inputProps, classes)}
                      className={`${classes.rootDialog} ${classes.dateField}`}
                      label="Start Date"
                      style={{ width: "100%", margin: 0, padding: 0 }}
                    />
                  )}
                </DatePicker>
                <DatePicker
                  autoOk
                  variant="inline"
                  label="End Date"
                  value={values.endDate ? new Date(values.endDate) : null}
                  onChange={onDateChange("endDate")}
                  format="yyyy/MM/dd"
                  minDate={startDateValue || undefined}
                  maxDate={new Date()}
                  style={{ width: "100%" }}
                  PopoverProps={popoverProps}
                >
                  {inputProps => (
                    <TextField
                      {...mergeDatePickerInputProps(inputProps, classes)}
                      className={`${classes.rootDialog} ${classes.dateField}`}
                      label="End Date"
                      style={{ width: "100%", margin: 0, padding: 0 }}
                    />
                  )}
                </DatePicker>
              </div>
              {dateRangeError && (
                <Typography color="error" variant="caption" style={{ marginTop: 8 }}>
                  {dateRangeError}
                </Typography>
              )}
            </MuiPickersUtilsProvider>
          </Grid>

          <Grid item xs={12} className={classes.fieldSection} style={{ padding: 0 }}>
            <TextField
              variant="standard"
              className={`${classes.rootDialog} ${classes.fullWidthField}`}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              SelectProps={selectMenuProps}
              label="Status"
              select
              value={values.status}
              onChange={onChange("status")}
            >
              <MenuItem className={classes.dialogBoxDropDown} value="Success">
                Success
              </MenuItem>
              <MenuItem className={classes.dialogBoxDropDown} value="Failed">
                Failed
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <div className={classes.filterActions}>
          <Button
            className={classes.clearButton}
            color="secondary"
            disabled={!hasFilters}
            onClick={onClear}
            variant="contained"
          >
            Clear
          </Button>
          <Button
            className={classes.buttonStyle}
            color="primary"
            disabled={!hasFilters || Boolean(dateRangeError)}
            type="submit"
            variant="contained"
          >
            Search
          </Button>
        </div>
      </form>
    </div>
  );
}

NachaFilterPopover.propTypes = {
  values: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  searchText: PropTypes.string.isRequired,
  startDateValue: PropTypes.instanceOf(Date),
  endDateValue: PropTypes.instanceOf(Date),
  dateRangeError: PropTypes.string,
};

export default NachaFilterPopover;



